import { Buffer } from "buffer";

import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { useCodatAuth } from "@codat/client-cookie-manager";
import {
  Button,
  Card,
  TextInput,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import {
  ResetPasswordCreate,
  useGetResetPasswordOptions,
  useResetPassword,
} from "api/password";
import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "utils/useQuery";
import { useTitle } from "utils/useTitle";

import AuthPage from "components/AuthPage";
import PasswordInputWithValidation from "components/PasswordInputWithValidation";

import styles from "./ResetPassword.module.css";

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  const { addToast } = useToast();
  const history = useHistory();
  const query = useQuery();
  useTitle("Reset Password");
  const { sendEvent, sendPageViewEvent } = useAnalytics();
  const { userManager, userData, authType } = useCodatAuth();

  const [{ loading: resetPasswordLoading }, resetPassword] = useResetPassword();
  const [
    {
      loading: passwordOptionsLoading,
      error: passwordOptionsError,
      data: passwordOptions,
    },
    getPasswordOptions,
  ] = useGetResetPasswordOptions(
    email || encodeURIComponent("test@email.co") // prevents hook from erroring when email has not been set
  );

  const resetPasswordCallback = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const token = query.get("token");
    if (!token) {
      addToast({
        message: `Auth token error. Please send a new reset password email.`,
        variant: "error",
      });
      return;
    }
    if (!passwordIsValid) {
      setDisplayErrors(true);
      return;
    }
    const decodedToken = Buffer.from(token, "base64").toString("utf8");
    const resetPasswordModel: ResetPasswordCreate = {
      email: email,
      password: password,
      confirmPassword: password,
      token: decodedToken,
    };

    resetPassword({ data: resetPasswordModel })
      .then(() => {
        addToast({
          message: `Password successfully reset!`,
          variant: "success",
        });

        // Ensure a currently logged in user is logged out
        if (authType === "OIDC") {
          userManager.revokeAccessToken();
          userManager.removeUser();
          return userManager.signoutRedirect({
            id_token_hint: userData?.id_token,
          });
        }
      })
      .catch(() => {
        addToast({
          message: `Password could not be reset`,
          variant: "error",
        });
      });
  };

  useEffect(() => {
    const emailQueryParam = query.get("email");
    if (emailQueryParam) {
      setEmail(emailQueryParam);
    }
  }, [query, history, getPasswordOptions]);

  const fetchPasswordOptions = useCallback(() => {
    if (email === "") return;
    getPasswordOptions().catch(() => {
      // error is returned in query above but a catch is needed here as calling manually
    });
  }, [email, getPasswordOptions]);

  useEffect(() => {
    fetchPasswordOptions();
  }, [fetchPasswordOptions]);

  useEffect(() => {
    sendPageViewEvent("Reset Password");
  }, [sendPageViewEvent]);

  return (
    <AuthPage>
      <Card className={styles.resetPasswordCard}>
        <form onSubmit={resetPasswordCallback}>
          <Typography variant="heading_xl" as="h1" className={styles.heading}>
            Reset password
          </Typography>
          {/* Hidden email input for accessibility */}
          <TextInput
            className={styles.emailInput}
            data-testid="reset-password-email-input"
            value={email}
            label="Email"
            id="hidden-email-input"
            type="email"
            readOnly
          />
          <PasswordInputWithValidation
            className={styles.passwordInputsWrapper}
            data-testid="reset-password-password-input"
            setPassword={setPassword}
            isPasswordValid={setPasswordIsValid}
            displayErrors={displayErrors}
            loading={passwordOptionsLoading}
            passwordOptionsError={passwordOptionsError}
            passwordOptions={passwordOptions}
          />
          <Button
            data-testid="reset-password-submit-button"
            className={styles.submitButton}
            label="Reset password"
            isLoading={resetPasswordLoading}
            onClick={() => sendEvent("Submit Button - Click")}
            type="submit"
          />
        </form>
      </Card>
    </AuthPage>
  );
};

export default function ResetPasswordWithTracking() {
  return (
    <EventContextProvider name="Reset Password">
      <ResetPassword />
    </EventContextProvider>
  );
}
